// src/pages/CustomerManagement/CustomerManagement.tsx

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import styles from "./CustomerManagement.module.scss";
import InvoicesTable from "./InvoicesTable";

interface User {
  id: number;
  full_name: string;
  promo_code: string;
  email: string;
  ssn_last4: string;
  spouse_id: string;
  taxdome_id: string;
  discount: boolean;
  appointment: boolean;
  app_time: string;
  createdat: string;
  updatedat: string;
}

const PAGE_SIZE = 25;

const CustomerManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // 当前激活的标签页： 'customers' 或 'invoices'
  const [activeTab, setActiveTab] = useState<"customers" | "invoices">(
    "customers"
  );

  // 实现搜索输入的防抖
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // 当搜索改变时，重置到第一页
    }, 500); // 500ms防抖

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (activeTab === "customers") {
      const fetchCustomers = async () => {
        try {
          const offset = page * PAGE_SIZE;
          let url = `/customers?offset=${offset}&limit=${PAGE_SIZE}`;
          if (debouncedSearch.trim() !== "") {
            const encodedSearch = encodeURIComponent(debouncedSearch.trim());
            url += `&search=${encodedSearch}`;
          }
          const res = await api.get(url);
          setUsers(res.data.users);
          setTotalCount(res.data.total);
        } catch (error) {
          console.error("Failed to load users:", error);
        }
      };

      fetchCustomers();
    }
  }, [page, debouncedSearch, activeTab]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleAddUser = () => {
    navigate("/customer_management/add");
  };

  const handleAddInvoice = () => {
    navigate("/customer_management/add-invoice");
  };

  const handlePrevious = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTabChange = (tab: "customers" | "invoices") => {
    setActiveTab(tab);
    setSearchQuery(""); // 清空搜索
    setDebouncedSearch("");
    setPage(0);
  };

  return (
    <div className={styles.customerManagementContainer}>
      <h1>{t("customerManagement.title")}</h1>

      {/* 标签页导航 */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${
            activeTab === "customers" ? styles.activeTab : ""
          }`}
          onClick={() => handleTabChange("customers")}
        >
          <span>Customers Tab</span>
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === "invoices" ? styles.activeTab : ""
          }`}
          onClick={() => handleTabChange("invoices")}
        >
          <span>Invoices Tab</span>
        </button>
      </div>

      {/* 上部工具栏 */}
      <div className={styles.topBar}>
        {/* 左侧显示总用户数 */}
        {activeTab === "customers" && (
          <div className={styles.totalUsers}>
            <strong>{t("customerManagement.totalUsers")}:</strong> {totalCount}
          </div>
        )}
        {/* 按钮组 */}
        <div className={styles.topBarButtons}>
          {activeTab === "customers" && (
            <button onClick={handleAddUser} className={styles.addButton}>
              {t("customerManagement.addUser")}
            </button>
          )}
          {/* 在 Invoices 标签页下不再显示 Add Invoice 按钮 */}
        </div>
      </div>

      {/* 内容区域 */}
      {activeTab === "customers" ? (
        <>
          {/* 搜索框 */}
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder={t("customerManagement.searchPlaceholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.userTable}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("customerManagement.fullName")}</th>
                  <th>{t("customerManagement.promoCode")}</th>
                  <th>{t("customerManagement.email")}</th>
                  <th>{t("customerManagement.ssnLast4")}</th>
                  <th>{t("customerManagement.spouseId")}</th>
                  <th>{t("customerManagement.taxdomeID")}</th>
                  <th>{t("customerManagement.applyDiscount")}</th>
                  <th>{t("customerManagement.appTime")}</th>
                  <th>{t("customerManagement.appointment")}</th>
                  <th>{t("customerManagement.createDate")}</th>
                  <th>{t("customerManagement.editDate")}</th>
                  <th>{t("customerManagement.edit")}</th> {/* 新增编辑列 */}
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.full_name}</td>
                      <td>{user.promo_code}</td>
                      <td>{user.email}</td>
                      <td>{user.ssn_last4 || ""}</td>
                      <td>{user.spouse_id}</td>
                      <td>{user.taxdome_id}</td>
                      <td>
                        {user.discount ? t("general.yes") : t("general.no")}
                      </td>
                      <td>
                        {user.app_time
                          ? new Date(user.app_time).toLocaleString()
                          : ""}
                      </td>
                      <td>
                        {user.appointment ? t("general.yes") : t("general.no")}
                      </td>
                      <td>{new Date(user.createdat).toLocaleString()}</td>
                      <td>{new Date(user.updatedat).toLocaleString()}</td>
                      <td>
                        <button
                          onClick={() =>
                            navigate(
                              `/customer_management/edit/customer/${user.id}`
                            )
                          }
                          className={styles.editButton}
                        >
                          {t("customerManagement.edit")}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={12} className={styles.noUsersFound}>
                      {t("customerManagement.noUsersFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          <div className={styles.pagination}>
            <button onClick={handlePrevious} disabled={page === 0}>
              {t("customerManagement.prev")}
            </button>
            <span>
              {t("customerManagement.page")} {page + 1}{" "}
              {t("customerManagement.of")} {totalPages}
            </span>
            <button onClick={handleNext} disabled={page >= totalPages - 1}>
              {t("customerManagement.next")}
            </button>
          </div>
        </>
      ) : (
        <InvoicesTable onAddInvoice={handleAddInvoice} />
      )}
    </div>
  );
};

export default CustomerManagement;
